import { useEffect, useState } from 'react'
import { useNotify, useUpdate, useDataProvider } from 'react-admin'

import styles from './Info.module.scss'
import { TeamMemberIcon } from '@icons/index.ts'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import { CircularProgress, Dialog } from '@mui/material'
import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import { formatDate } from '@src/utils/dateFormatter'
import { ToggleButton } from '@components/toggleButton/toggleButton.tsx'
import { LxTooltip } from '@src/components/tooltip/tooltip'
import { LxIcon } from '@src/components/icon/Icon'
import { InfoOutlineIcon } from '@src/assets/icons/utils'
import { mapStateToReadableStatus, State } from '@src/utils/mapStateToReadableState'
import { USER_STATE } from '@src/utils/constants'
import { cn } from '@src/utils/cn'

const customStyles = {
  smallSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '40px',
    height: '24px',
  },
}

export type UserPropsB = {
  id: string
  first_name: string
  last_name: string
  primary_email: string
  last_login: string
  state: State
}

export type UserPropsF = {
  id: string
  firstName: string
  lastName: string
  primaryEmail: string
  lastLogin: string
  state: State
}

interface UserInfoProps {
  accountId: string
  accountName: string
}

const UserInfo: React.FC<UserInfoProps> = ({ accountId, accountName }) => {
  const [showDialog, setShowDialog] = useState(false)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [userData, setUserData] = useState<[UserPropsF] | null>(null)
  const [processingUserId, setProcessingUserId] = useState<string | null>(null)
  const dataProvider = useDataProvider()

  const notify = useNotify()
  const [update] = useUpdate('Report')

  const sanitizeData = (data: any) => {
    if (!data || !data.report) {
      return null
    }

    return data.report.map((user: UserPropsB) => ({
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      primaryEmail: user.primary_email,
      state: user.state,
      lastLoginFormatted: user.last_login ? formatDate(user.last_login) : 'N/A',
    }))
  }

  const fetchData = async () => {
    setIsDataLoading(true)
    try {
      await update(
        'Report',
        {
          id: accountId,
          data: {
            filter: {},
            type: 'user_info',
          },
          previousData: null,
        },
        {
          onSuccess: (data) => {
            const sanitizedData = sanitizeData(data)
            setUserData(sanitizedData)
            setIsDataLoading(false)
          },
          onError: (error) => {
            setUserData(null)
            const errorMessage = (error as { message?: string }).message || 'Something went wrong'
            notify(errorMessage, { type: 'warning' })
            setIsDataLoading(false)
          },
        }
      )
    } catch (error) {
      setIsDataLoading(false)
    }
  }

  useEffect(() => {
    if (showDialog) {
      fetchData()
    }
  }, [showDialog])

  const handleToggleUserStatus = async (userId: string, currentState: string) => {
    if (processingUserId) {
      notify('Another user is being processed. Please wait.', { type: 'warning' })
      return
    }
    // Set the processing state to show loading for this specific user
    setProcessingUserId(userId)

    // Determine the new state based on current state
    const newState = (currentState === USER_STATE.ACTIVE) ? USER_STATE.LOCKED : USER_STATE.ACTIVE

    try {
      await dataProvider.update('User', {
        id: userId,
        data: {
          update_state: {
            action: 'updateUserState',
            state: newState,
          },
        },
        previousData: null,
      })

      // Update the local state to reflect the change
      if (userData) {
        const updatedUserData = userData.map((user) => {
          if (user.id === userId) {
            return { ...user, state: newState }
          }
          return user
        })
        setUserData(updatedUserData as [UserPropsF])
      }

      if (newState === USER_STATE.ACTIVE) {
        notify('User unlocked successfully', { type: 'success' })
      } else {
        notify('User locked successfully', { type: 'success' })
      }
    } catch (error) {
      const errorMessage = (error as { message?: string }).message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'warning' })
    } finally {
      setProcessingUserId(null)
    }
  }

  return (
    <>
      <LxClickAbleIcon
        onClick={() => {
          setShowDialog(true)
        }}
        icon={TeamMemberIcon}
        ariaLabel={`Administrators Info for ${accountName}`}
        role='button'
      ></LxClickAbleIcon>
      <Dialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false)
        }}
        maxWidth='md'
        fullWidth
        role='dialog'
      >
        <div className={styles.dialogHeader}>
          <h3>Users of {accountName}</h3>
        </div>
        <div className={styles.userDialogContent}>
          <div className={cn(styles.userHeaderRow, styles.headerRow)}>
            <strong>First Name</strong>
            <strong>Last Name</strong>
            <strong>Email</strong>
            <strong>Status</strong>
            <span className={styles.headerWithIcon}>
              <strong>Active</strong>
              <LxTooltip tooltipText={'Temporarily prevent the user login without data loss. To remove users completely, use Manage Status in Team Members.'}>
                <LxIcon icon={InfoOutlineIcon} />
              </LxTooltip>
            </span>
          </div>

          <div className={styles.contentContainer}>
            {isDataLoading ? (
              <div style={{ padding: '32px', display: 'flex', justifyContent: 'center' }}>
                <LxLoadingSpinner />
              </div>
            ) : (
              userData?.map((user) => (
                <div className={cn(styles.userInfoRow, styles.infoRow)} key={`user-row-${user.id}`}>
                  <span data-label='First Name:'>{user?.firstName}</span>
                  <span data-label='Last Name:'>{user?.lastName}</span>
                  <span data-label='Email:' title={user?.primaryEmail}>
                    {user?.primaryEmail}
                  </span>
                  <span data-label='Status:'>{mapStateToReadableStatus(user?.state)}</span>
                  <span className={styles.actionCell} data-label='Lock/Unlock:'>
                    {processingUserId === user.id ? (
                      <div style={customStyles.smallSpinner}>
                        <CircularProgress size={20} />
                      </div>
                    ) : (
                      <ToggleButton
                        value={[USER_STATE.ACTIVE, USER_STATE.INVITED, USER_STATE.INVITE_DELIVERED, USER_STATE.INVITE_FAILED].includes(user?.state)}
                        onChange={() => handleToggleUserStatus(user.id, user.state)}
                        ariaLabel={`${[USER_STATE.ACTIVE, USER_STATE.INVITED, USER_STATE.INVITE_DELIVERED, USER_STATE.INVITE_FAILED].includes(user?.state) ? 'Lock' : 'Unlock'} User`}
                      />
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className={styles.dialogActions}>
          <LxCancelButton
            label='Close'
            classNames='lxActionButton,lxActionButtonDefaultSize'
            onCancel={() => {
              setShowDialog(false)
            }}
          />
        </div>
      </Dialog>
    </>
  )
}

export default UserInfo
